import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import { toast } from "react-toastify";
import Listtable from "components/common/ListTable";
import { dentalDiagnosisMasterApi } from "api/dentalWorkspaceMaster";
import DentalWorkspaceMasterModal from "./DentalWorkspaceMasterModal";
import {
  SHEET_FILES,
  importFailures,
  importSummary,
  reactivatePayload,
} from "./dentalWorkspaceMasterForm";

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  boxShadow: "none",
  borderRadius: "8px",
  bgcolor: "#194AF5",
  color: "white",
};

const secondaryBtnStyle = {
  ...btnStyle,
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const statusChip = (isActive) => (
  <Chip
    size="small"
    label={isActive === false ? "Inactive" : "Active"}
    color={isActive === false ? "default" : "success"}
    variant="outlined"
  />
);

// No Code column (removed 2026-10-15 with the Code and Display order fields).
const config = {
  noun: "Diagnosis",
  searchLabel: "Search diagnoses",
  headers: ["Diagnosis", "Category", "Site type", "Status", "Actions"],
  keys: ["name", "category", "siteType", "statusCell", "action"],
};

const api = dentalDiagnosisMasterApi;

const DentalWorkspaceMaster = () => {

  const [rawRows, setRawRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState({});
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchRows = useCallback(() => {
    setLoading(true);
    api
      .list({
        page: pageNumber + 1,
        perPage: rowsPerPage,
        includeInactive: true,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRawRows(list);
        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || `Could not load ${config.noun.toLowerCase()} rows.`);
      })
      .finally(() => setLoading(false));
  }, [pageNumber, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleReactivate = useCallback(
    async (item) => {
      setBusy(true);
      try {
        await api.update(item._id, reactivatePayload(item));
        toast.success(`${config.noun} reactivated successfully`);
        fetchRows();
      } catch (err) {
        toast.error(err?.response?.data?.message || `Could not reactivate this ${config.noun.toLowerCase()}.`);
      } finally {
        setBusy(false);
      }
    },
    [fetchRows]
  );

  const rows = useMemo(
    () =>
      rawRows.map((item) => ({
        ...item,
        statusCell: statusChip(item.isActive),
        action: (
          <Box sx={{ display: "flex", gap: "0.25rem" }}>
            <Tooltip title="Edit">
              <IconButton
                aria-label={`Edit ${item.name}`}
                onClick={() => {
                  setActiveRow(item);
                  setModalOpen(true);
                }}
              >
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            {item.isActive === false ? (
              <Tooltip title="Reactivate">
                <IconButton
                  aria-label={`Reactivate ${item.name}`}
                  onClick={() => handleReactivate(item)}
                >
                  <RestoreIcon fontSize="small" color="success" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Deactivate">
                <IconButton
                  aria-label={`Deactivate ${item.name}`}
                  onClick={() => setDeactivateTarget(item)}
                >
                  <BlockIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      })),
    [rawRows, handleReactivate]
  );

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveRow({});
    if (reason !== "close") fetchRows();
  };

  // Template and export are built by the server, so both apps share one sheet contract.
  const handleDownload = async (kind) => {
    setBusy(true);
    try {
      const response = await api.downloadFile(
        kind,
        kind === "export"
          ? { includeInactive: true, ...(debouncedSearch ? { search: debouncedSearch } : {}) }
          : {}
      );
      saveBlob(response.data, kind === "export" ? SHEET_FILES.export : SHEET_FILES.template);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await api.importFile(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);

      // Failed rows need their reasons, which a toast cannot hold.
      if (failures.length > 0) {
        setImportResult({ message: summary.message, failures });
      } else if (summary.level === "success") {
        toast.success(summary.message);
      } else {
        toast.info(summary.message);
      }

      setPageNumber(0);
      fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget?._id) return;
    setBusy(true);
    try {
      await api.remove(deactivateTarget._id);
      toast.success(`${config.noun} deactivated successfully`);
      fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Could not deactivate this ${config.noun.toLowerCase()}.`);
    } finally {
      setBusy(false);
      setDeactivateTarget(null);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label={config.searchLabel}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 260, mr: "auto" }}
        />

        <Button
          sx={secondaryBtnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("template")}
        >
          Download Template
        </Button>

        <Button
          sx={secondaryBtnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          ref={fileInputRef}
          hidden
          data-testid="dental-diagnosis-import-input"
          onChange={handleImport}
        />

        <Button
          sx={secondaryBtnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("export")}
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, ":hover": { bgcolor: "#1540d6" } }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveRow({});
            setModalOpen(true);
          }}
        >
          Add {config.noun}
        </Button>
      </Box>

      <Listtable
        tableheader={config.headers}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={config.keys}
        tag="dentalWorkspace-diagnosis"
      />

      {modalOpen && (
        <DentalWorkspaceMasterModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeRow}
          api={api}
          noun={config.noun}
        />
      )}

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>{importResult?.message}</DialogContentText>
          <Box sx={{ fontWeight: 600, mb: 1 }}>Failed rows:</Box>
          <Box
            component="ul"
            aria-label="Failed rows"
            sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 320, overflowY: "auto" }}
          >
            {(importResult?.failures ?? []).map((item, index) => (
              <Box
                component="li"
                key={`${item.row}-${item.name}-${index}`}
                sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {item.row ? `Row ${item.row}` : "Row"}
                  {item.name ? ` — ${item.name}` : ""}
                </Box>
                <Box sx={{ color: "#d32f2f" }}>Reason: {item.reason}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate {config.noun.toLowerCase()}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deactivateTarget?.name}" will stop appearing for every clinic. You can
            reactivate it later from this list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDeactivate} color="error" disabled={busy}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default DentalWorkspaceMaster;
